// ViewInformationProtection.jsx
import React, { useState } from "react";
import "./ViewInformationProtection.scss";
import { usePDF } from "react-to-pdf";

const ViewInformationProtection = () => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const { toPDF, targetRef } = usePDF({
    filename: `sistema-proteccion-informacion-${
      new Date().toISOString().split("T")[0]
    }.pdf`,
    page: {
      margin: 20,
      format: "A4",
      orientation: "portrait",
    },
    canvas: {
      // Configuraciones críticas para mantener consistencia
      mimeType: "image/png",
      qualityRatio: 1,
      useCORS: true,
      allowTaint: true,
      // Clave: Configurar scaling para evitar desplazamientos
      scale: 2,
      scrollX: 0,
      scrollY: 0,
      // Forzar dimensiones específicas
      width: 794, // A4 width en pixels a 96 DPI
      height: 1123, // A4 height en pixels a 96 DPI
    },
    jsPDF: {
      unit: "px",
      format: "a4",
      orientation: "portrait",
      compress: true,
    },
  });

  const handlePrintPreview = () => {
    setIsPreviewMode(true);
    setTimeout(() => window.print(), 100);
  };

const handlePDFGeneration = () => {
  // Pre-procesamiento para normalizar estilos
  setIsPreviewMode(true);
  
  // Timeout para asegurar que los estilos se apliquen
  setTimeout(() => {
    toPDF().then(() => {
      setIsPreviewMode(false);
    });
  }, 500);
};

  return (
    <div
      className={`information-protection-container ${
        isPreviewMode ? "print-mode" : ""
      }`}
    >
      {/* Header Institucional */}
      <div className="protection-header">
        <div className="company-brand">
          <div className="brand-logo">
            <span className="logo-icon">🛡️</span>
          </div>
          <div className="brand-info">
            <h1>Sistema de Protección de la Información</h1>
            <p className="company-name">ContrasPetrols</p>
            <p className="subtitle">
              Marco institucional para la protección integral de datos y
              seguridad de la información
            </p>
          </div>
        </div>

        <div className="document-meta">
          <div className="meta-item">
            <span className="meta-label">Documento:</span>
            <span className="meta-value">SPI-001-2025</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Versión:</span>
            <span className="meta-value">3.1</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Fecha:</span>
            <span className="meta-value">
              {new Date().toLocaleDateString("es-CO")}
            </span>
          </div>
        </div>

        {/* Controles de Documento */}
        <div className="document-controls">
          <button
            className="control-btn preview-btn"
            onClick={handlePrintPreview}
            type="button"
          >
            <span className="btn-icon">👁️</span>
            Vista Previa
          </button>
          <button
            className="control-btn pdf-btn"
            onClick={handlePDFGeneration}
            type="button"
          >
            <span className="btn-icon">📄</span>
            Generar PDF
          </button>
        </div>
      </div>

      {/* Contenido Principal */}
      <div ref={targetRef} className="protection-content">
        {/* 1. Introducción Institucional */}
        <section className="protection-section introduction-section">
          <div className="section-header">
            <h2>1. Introducción</h2>
            <div className="section-badge">Fundamentos</div>
          </div>

          <div className="section-content">
            <div className="institutional-intro">
              <p className="intro-text">
                ContrasPetrols establece el presente Sistema de Protección de la
                Información como marco institucional integral para garantizar la
                confidencialidad, integridad y disponibilidad de todos los
                activos de información de la organización.
              </p>

              <div className="commitment-box">
                <h4>Compromiso Institucional</h4>
                <p>
                  Nuestra organización se compromete a implementar las mejores
                  prácticas de seguridad de la información, cumpliendo con la
                  normatividad vigente y estableciendo controles efectivos para
                  la protección de datos.
                </p>
              </div>
            </div>

            <div className="principles-grid">
              <div className="principle-card">
                <div className="principle-icon confidentiality">🔒</div>
                <h4>Confidencialidad</h4>
                <p>Protección contra divulgación no autorizada</p>
              </div>
              <div className="principle-card">
                <div className="principle-icon integrity">✓</div>
                <h4>Integridad</h4>
                <p>Mantenimiento de exactitud y completitud</p>
              </div>
              <div className="principle-card">
                <div className="principle-icon availability">⚡</div>
                <h4>Disponibilidad</h4>
                <p>Acceso oportuno cuando sea requerido</p>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Alcance y Objetivos Estratégicos */}
        <section className="protection-section scope-section">
          <div className="section-header">
            <h2>2. Alcance y Objetivos Estratégicos</h2>
            <div className="section-badge">Estratégico</div>
          </div>

          <div className="section-content">
            <div className="scope-definition">
              <h3>Alcance Institucional</h3>
              <div className="scope-areas">
                <div className="scope-area primary">
                  <h4>Activos de Información Críticos</h4>
                  <ul>
                    <li>Bases de datos operativas y administrativas</li>
                    <li>Sistemas de gestión de flotas vehiculares</li>
                    <li>Información financiera y contable</li>
                    <li>Datos personales de clientes y empleados</li>
                  </ul>
                </div>

                <div className="scope-area secondary">
                  <h4>Infraestructura Tecnológica</h4>
                  <ul>
                    <li>Servidores y equipos de comunicaciones</li>
                    <li>Aplicaciones y sistemas de información</li>
                    <li>Redes de datos y telecomunicaciones</li>
                    <li>Dispositivos móviles y endpoints</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="strategic-objectives">
              <h3>Objetivos Estratégicos</h3>
              <div className="objectives-timeline">
                <div className="objective-item short-term">
                  <div className="timeline-marker"></div>
                  <div className="objective-content">
                    <h4>Corto Plazo (0-6 meses)</h4>
                    <p>
                      Implementación de controles básicos de seguridad y
                      capacitación inicial del personal
                    </p>
                  </div>
                </div>

                <div className="objective-item medium-term">
                  <div className="timeline-marker"></div>
                  <div className="objective-content">
                    <h4>Mediano Plazo (6-18 meses)</h4>
                    <p>
                      Certificación en estándares internacionales y
                      automatización de procesos de seguridad
                    </p>
                  </div>
                </div>

                <div className="objective-item long-term">
                  <div className="timeline-marker"></div>
                  <div className="objective-content">
                    <h4>Largo Plazo (18+ meses)</h4>
                    <p>
                      Madurez en gestión de riesgos y liderazgo sectorial en
                      seguridad de la información
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Clasificación Institucional de la Información */}
        <section className="protection-section classification-section">
          <div className="section-header">
            <h2>3. Clasificación Institucional de la Información</h2>
            <div className="section-badge">Normativo</div>
          </div>

          <div className="section-content">
            <div className="classification-framework">
              <div className="classification-level critical-level">
                <div className="level-header">
                  <span className="level-badge critical">CRÍTICA</span>
                  <span className="level-code">Nivel I</span>
                </div>
                <div className="level-content">
                  <h4>Información de Máxima Seguridad</h4>
                  <p>
                    Información cuya divulgación podría causar daño severo a la
                    organización
                  </p>
                  <div className="examples-grid">
                    <div className="example-item">
                      🔐 Credenciales administrativas
                    </div>
                    <div className="example-item">
                      💰 Información financiera estratégica
                    </div>
                    <div className="example-item">
                      📊 Datos de inteligencia de negocio
                    </div>
                  </div>
                </div>
              </div>

              <div className="classification-level confidential-level">
                <div className="level-header">
                  <span className="level-badge confidential">CONFIDENCIAL</span>
                  <span className="level-code">Nivel II</span>
                </div>
                <div className="level-content">
                  <h4>Información Sensible del Negocio</h4>
                  <p>
                    Información que requiere protección especial por su
                    sensibilidad
                  </p>
                  <div className="examples-grid">
                    <div className="example-item">
                      👥 Datos personales de clientes
                    </div>
                    <div className="example-item">
                      🚛 Información de rutas y operaciones
                    </div>
                    <div className="example-item">
                      📋 Contratos y acuerdos comerciales
                    </div>
                  </div>
                </div>
              </div>

              <div className="classification-level internal-level">
                <div className="level-header">
                  <span className="level-badge internal">INTERNA</span>
                  <span className="level-code">Nivel III</span>
                </div>
                <div className="level-content">
                  <h4>Información de Uso Interno</h4>
                  <p>
                    Información para uso exclusivo del personal de la
                    organización
                  </p>
                  <div className="examples-grid">
                    <div className="example-item">
                      📚 Manuales de procedimientos
                    </div>
                    <div className="example-item">
                      📧 Comunicaciones internas
                    </div>
                    <div className="example-item">
                      📈 Reportes operativos internos
                    </div>
                  </div>
                </div>
              </div>

              <div className="classification-level public-level">
                <div className="level-header">
                  <span className="level-badge public">PÚBLICA</span>
                  <span className="level-code">Nivel IV</span>
                </div>
                <div className="level-content">
                  <h4>Información de Dominio Público</h4>
                  <p>Información que puede ser compartida sin restricciones</p>
                  <div className="examples-grid">
                    <div className="example-item">
                      🌐 Información corporativa general
                    </div>
                    <div className="example-item">
                      📞 Datos de contacto públicos
                    </div>
                    <div className="example-item">📄 Políticas públicas</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Responsabilidades */}
        <section className="protection-section">
          <h2>4. Responsabilidades</h2>
          <div className="section-content">
            <div className="responsibilities-grid">
              <div className="responsibility-card">
                <h4>Administradores del Sistema</h4>
                <ul>
                  <li>Implementar y mantener medidas de seguridad</li>
                  <li>Monitorear accesos y actividades sospechosas</li>
                  <li>Realizar respaldos regulares de la información</li>
                  <li>Actualizar sistemas y parches de seguridad</li>
                </ul>
              </div>

              <div className="responsibility-card">
                <h4>Usuarios Finales</h4>
                <ul>
                  <li>Proteger sus credenciales de acceso</li>
                  <li>No compartir información sensible</li>
                  <li>Reportar incidentes de seguridad</li>
                  <li>Cumplir con las políticas establecidas</li>
                </ul>
              </div>

              <div className="responsibility-card">
                <h4>Equipo de Desarrollo</h4>
                <ul>
                  <li>Implementar prácticas de desarrollo seguro</li>
                  <li>Realizar auditorías de código</li>
                  <li>Mantener actualizadas las dependencias</li>
                  <li>Documentar cambios y actualizaciones</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Normas de Seguridad */}
        <section className="protection-section">
          <h2>5. Normas de Seguridad</h2>
          <div className="section-content">
            <h3>Seguridad Física</h3>
            <div className="security-grid">
              <div className="security-item">
                <h4>🔐 Control de Acceso Físico</h4>
                <p>
                  Acceso restringido a áreas donde se almacenan servidores y
                  equipos críticos
                </p>
              </div>
              <div className="security-item">
                <h4>📹 Sistemas de Vigilancia</h4>
                <p>Monitoreo continuo de instalaciones críticas</p>
              </div>
              <div className="security-item">
                <h4>🔥 Protección contra Incendios</h4>
                <p>Sistemas de detección y extinción de incendios</p>
              </div>
              <div className="security-item">
                <h4>⚡ Sistemas de Energía</h4>
                <p>UPS y generadores de respaldo para continuidad operativa</p>
              </div>
            </div>

            <h3>Seguridad Digital</h3>
            <div className="digital-security">
              <div className="security-measure">
                <h4>🔑 Autenticación Multi-Factor</h4>
                <p>
                  Implementamos autenticación de múltiples factores para accesos
                  críticos
                </p>
              </div>

              <div className="security-measure">
                <h4>🛡️ Encriptación de Datos</h4>
                <p>
                  Todos los datos sensibles se almacenan encriptados usando
                  algoritmos AES-256
                </p>
              </div>

              <div className="security-measure">
                <h4>🌐 HTTPS/TLS</h4>
                <p>
                  Todas las comunicaciones se realizan a través de conexiones
                  seguras
                </p>
              </div>

              <div className="security-measure">
                <h4>🤖 reCAPTCHA</h4>
                <p>
                  Protección contra bots y ataques automatizados en formularios
                  críticos
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Control de Accesos */}
        <section className="protection-section">
          <h2>6. Control de Accesos</h2>
          <div className="section-content">
            <div className="access-control-info">
              <h3>Política de Acceso Basada en Roles</h3>
              <p>
                Implementamos un sistema de control de acceso basado en roles
                (RBAC) que asigna permisos específicos según la función del
                usuario en la organización.
              </p>

              <div className="roles-grid">
                <div className="role-card">
                  <h4>👑 Administrador</h4>
                  <p>
                    Acceso completo al sistema con capacidades de gestión de
                    usuarios y configuración
                  </p>
                </div>
                <div className="role-card">
                  <h4>🚛 Operador</h4>
                  <p>
                    Acceso limitado a funciones operativas específicas de su
                    flota
                  </p>
                </div>
                <div className="role-card">
                  <h4>👥 Recursos Humanos</h4>
                  <p>
                    Acceso a información de personal y reportes de asistencia
                  </p>
                </div>
                <div className="role-card">
                  <h4>📊 ADO</h4>
                  <p>Acceso a reportes y análisis de datos operativos</p>
                </div>
              </div>

              <h3>Gestión de Sesiones</h3>
              <ul className="session-management">
                <li>Tokens JWT con expiración automática (5 horas)</li>
                <li>Renovación automática de tokens antes de expirar</li>
                <li>Lista negra de tokens revocados</li>
                <li>Cierre de sesión automático por inactividad</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 7. Respaldo y Recuperación */}
        <section className="protection-section">
          <h2>7. Respaldo y Recuperación</h2>
          <div className="section-content">
            <div className="backup-strategy">
              <h3>Estrategia de Respaldo</h3>
              <div className="backup-types">
                <div className="backup-type">
                  <h4>🔄 Respaldo Completo</h4>
                  <p>Realizado semanalmente, incluye toda la base de datos</p>
                </div>
                <div className="backup-type">
                  <h4>📝 Respaldo Incremental</h4>
                  <p>
                    Realizado diariamente, incluye solo cambios desde el último
                    respaldo
                  </p>
                </div>
                <div className="backup-type">
                  <h4>💾 Respaldo en la Nube</h4>
                  <p>
                    Almacenamiento seguro en servicios cloud con encriptación
                  </p>
                </div>
              </div>

              <h3>Plan de Recuperación</h3>
              <div className="recovery-plan">
                <div className="recovery-step">
                  <span className="step-number">1</span>
                  <div className="step-content">
                    <h4>Evaluación del Incidente</h4>
                    <p>Identificar la naturaleza y alcance del problema</p>
                  </div>
                </div>
                <div className="recovery-step">
                  <span className="step-number">2</span>
                  <div className="step-content">
                    <h4>Activación del Plan</h4>
                    <p>
                      Notificar al equipo de respuesta y activar procedimientos
                    </p>
                  </div>
                </div>
                <div className="recovery-step">
                  <span className="step-number">3</span>
                  <div className="step-content">
                    <h4>Restauración de Datos</h4>
                    <p>
                      Recuperar desde el respaldo más reciente y verificar
                      integridad
                    </p>
                  </div>
                </div>
                <div className="recovery-step">
                  <span className="step-number">4</span>
                  <div className="step-content">
                    <h4>Verificación y Pruebas</h4>
                    <p>Confirmar que el sistema funciona correctamente</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Gestión de Incidentes */}
        <section className="protection-section">
          <h2>8. Gestión de Incidentes</h2>
          <div className="section-content">
            <div className="incident-management">
              <h3>Proceso de Respuesta a Incidentes</h3>

              <div className="incident-phases">
                <div className="phase">
                  <h4>🚨 Detección</h4>
                  <p>
                    Identificación del incidente a través de monitoreo
                    automático o reportes
                  </p>
                </div>
                <div className="phase">
                  <h4>📞 Notificación</h4>
                  <p>
                    Comunicación inmediata al equipo de seguridad y
                    administradores
                  </p>
                </div>
                <div className="phase">
                  <h4>🔍 Análisis</h4>
                  <p>Evaluación del impacto y determinación de la causa raíz</p>
                </div>
                <div className="phase">
                  <h4>🛠️ Contención</h4>
                  <p>
                    Aplicación de medidas para limitar el alcance del incidente
                  </p>
                </div>
                <div className="phase">
                  <h4>🔧 Resolución</h4>
                  <p>Eliminación de la amenaza y restauración de servicios</p>
                </div>
                <div className="phase">
                  <h4>📋 Documentación</h4>
                  <p>Registro detallado del incidente y lecciones aprendidas</p>
                </div>
              </div>

              <div className="contact-info">
                <h3>Contacto para Reportar Incidentes</h3>
                <div className="contact-cards">
                  <div className="contact-card">
                    <h4>📧 Email de Seguridad</h4>
                    <p>security@contraspetrols.com</p>
                  </div>
                  <div className="contact-card">
                    <h4>📱 Línea Directa</h4>
                    <p>+57 XXX XXX XXXX</p>
                  </div>
                  <div className="contact-card">
                    <h4>🎫 Ticket de Soporte</h4>
                    <p>Crear ticket en el sistema de ayuda</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 9. Capacitación */}
        <section className="protection-section">
          <h2>9. Capacitación</h2>
          <div className="section-content">
            <div className="training-program">
              <h3>Programa de Concientización en Seguridad</h3>

              <div className="training-modules">
                <div className="training-module">
                  <h4>📚 Fundamentos de Seguridad</h4>
                  <ul>
                    <li>Principios básicos de protección de información</li>
                    <li>Identificación de amenazas comunes</li>
                    <li>Mejores prácticas de contraseñas</li>
                  </ul>
                </div>

                <div className="training-module">
                  <h4>🖥️ Seguridad en el Uso de Sistemas</h4>
                  <ul>
                    <li>Uso seguro de la plataforma</li>
                    <li>Reconocimiento de phishing</li>
                    <li>Manejo seguro de datos sensibles</li>
                  </ul>
                </div>

                <div className="training-module">
                  <h4>📋 Cumplimiento y Políticas</h4>
                  <ul>
                    <li>Políticas de la organización</li>
                    <li>Regulaciones aplicables</li>
                    <li>Responsabilidades del usuario</li>
                  </ul>
                </div>
              </div>

              <div className="training-schedule">
                <h3>Cronograma de Capacitación</h3>
                <div className="schedule-grid">
                  <div className="schedule-item">
                    <span className="frequency">Mensual</span>
                    <p>Recordatorios de seguridad y mejores prácticas</p>
                  </div>
                  <div className="schedule-item">
                    <span className="frequency">Trimestral</span>
                    <p>Capacitación formal en temas específicos</p>
                  </div>
                  <div className="schedule-item">
                    <span className="frequency">Anual</span>
                    <p>
                      Evaluación de conocimientos y actualización de políticas
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 10. Revisión y Mejora */}
        <section className="protection-section">
          <h2>10. Revisión y Mejora</h2>
          <div className="section-content">
            <div className="review-process">
              <h3>Proceso de Revisión Continua</h3>

              <div className="review-areas">
                <div className="review-area">
                  <h4>🔍 Auditorías de Seguridad</h4>
                  <p>
                    Evaluaciones periódicas de la efectividad de las medidas de
                    seguridad
                  </p>
                </div>

                <div className="review-area">
                  <h4>📊 Análisis de Métricas</h4>
                  <p>Monitoreo de indicadores de seguridad y rendimiento</p>
                </div>

                <div className="review-area">
                  <h4>🔄 Actualización de Políticas</h4>
                  <p>
                    Revisión y actualización de políticas según cambios en
                    amenazas
                  </p>
                </div>

                <div className="review-area">
                  <h4>📈 Mejora Continua</h4>
                  <p>
                    Implementación de mejoras basadas en lecciones aprendidas
                  </p>
                </div>
              </div>

              <div className="improvement-cycle">
                <h3>Ciclo de Mejora</h3>
                <div className="cycle-steps">
                  <div className="cycle-step">
                    <span className="step-icon">📋</span>
                    <h4>Planificar</h4>
                    <p>Identificar áreas de mejora y establecer objetivos</p>
                  </div>
                  <div className="cycle-step">
                    <span className="step-icon">🛠️</span>
                    <h4>Implementar</h4>
                    <p>Ejecutar las mejoras planificadas</p>
                  </div>
                  <div className="cycle-step">
                    <span className="step-icon">📊</span>
                    <h4>Medir</h4>
                    <p>Evaluar la efectividad de las mejoras</p>
                  </div>
                  <div className="cycle-step">
                    <span className="step-icon">🔄</span>
                    <h4>Actuar</h4>
                    <p>Ajustar y optimizar basado en resultados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Información de Contacto */}
        <section className="protection-section contact-section">
          <h2>Información de Contacto</h2>
          <div className="section-content">
            <div className="contact-grid">
              <div className="contact-item">
                <h4>👨‍💼 Oficial de Seguridad de la Información</h4>
                <p>Email: ciso@contraspetrols.com</p>
                <p>Teléfono: +57 XXX XXX XXXX</p>
              </div>
              <div className="contact-item">
                <h4>🛠️ Soporte Técnico</h4>
                <p>Email: soporte@contraspetrols.com</p>
                <p>Teléfono: +57 XXX XXX XXXX</p>
              </div>
              <div className="contact-item">
                <h4>📋 Cumplimiento</h4>
                <p>Email: cumplimiento@contraspetrols.com</p>
                <p>Teléfono: +57 XXX XXX XXXX</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ViewInformationProtection;
